import "../css/style.css";

const homePages = document.querySelector(".home-pages");
if (homePages) setupPageScreens(homePages);

const hero = document.querySelector(".hero");
if (hero) setupHero(hero);

function setupHero(hero) {
  const scenes = hero.querySelectorAll("[data-hero-scene]");
  const controls = hero.querySelector(".hero-controls");
  const selectors = hero.querySelectorAll("[data-hero-select]");
  const copy = hero.querySelector(".hero-copy");
  const animations = new Map();
  let current = -1;
  let slideDirection = 1;
  let timer = null;
  let inView = false;
  let lastWheelAt = -Infinity;
  let wheelDistance = 0;
  let wheelConsumed = false;
  let touch = null;

  function showScene(index, { animate = true, direction = 1 } = {}) {
    if (index === current) return;
    const previous = current;
    const moving = animate && previous >= 0;
    // With two slides, an interrupted transition reverses the same pair of panels.
    if (moving && animations.size && selectors.length === 2) direction = -slideDirection;
    slideDirection = direction;
    // Capture the visible positions so another click can smoothly reverse an active slide.
    const positions = new Map([...scenes].map((scene) => {
      const style = getComputedStyle(scene);
      return [scene, { transform: style.transform, opacity: style.opacity, visibility: style.visibility }];
    }));
    animations.forEach((animation) => animation.cancel());
    animations.clear();
    current = index;
    hero.dataset.transition = moving ? "slide" : "instant";
    hero.dataset.direction = direction > 0 ? "next" : "previous";
    hero.dataset.scene = String(index + 1);
    const distance = copy.clientWidth;
    scenes.forEach((scene) => {
      const sceneIndex = Number(scene.dataset.heroScene);
      const active = sceneIndex === current;
      scene.classList.toggle("is-active", active);
      scene.setAttribute("aria-hidden", String(!active));
      if (!moving || (!active && sceneIndex !== previous)) return;

      const position = positions.get(scene);
      const from = position.visibility === "visible"
        ? position
        : { transform: `translate3d(${direction * distance}px, 0, 0)`, opacity: 0, visibility: "visible" };
      const animation = scene.animate([
        from,
        { transform: `translate3d(${active ? 0 : -direction * distance}px, 0, 0)`, opacity: active ? 1 : 0, visibility: "visible" },
      ], { duration: 680, easing: "cubic-bezier(0.22, 1, 0.36, 1)" });
      animations.set(scene, animation);
      animation.onfinish = () => {
        if (animations.get(scene) === animation) animations.delete(scene);
      };
    });
    selectors.forEach((button) => {
      button.setAttribute("aria-pressed", String(Number(button.dataset.heroSelect) === current));
    });
  }

  function syncPlayback() {
    if (timer !== null) clearInterval(timer);
    timer = null;
    const playing = inView && !document.hidden;
    hero.classList.toggle("is-playing", playing);
    if (playing) timer = setInterval(() => showScene((current + 1) % selectors.length), 9000);
  }

  selectors.forEach((button) => button.addEventListener("click", () => {
    const index = Number(button.dataset.heroSelect);
    showScene(index, { direction: index < current ? -1 : 1 });
    syncPlayback();
  }));

  function moveScene(direction) {
    showScene((current + direction + selectors.length) % selectors.length, { direction });
    syncPlayback();
  }

  // Capture horizontal trackpad input before the browser turns it into history navigation.
  hero.addEventListener("wheel", (event) => {
    if (event.ctrlKey || !event.deltaX || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
    event.preventDefault();
    const now = performance.now();
    if (now - lastWheelAt > 180) {
      wheelDistance = 0;
      wheelConsumed = false;
    }
    lastWheelAt = now;
    if (wheelConsumed) return;
    const direction = Math.sign(event.deltaX);
    if (Math.sign(wheelDistance) !== direction) wheelDistance = 0;
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? copy.clientWidth : 1;
    wheelDistance += event.deltaX * unit;
    if (Math.abs(wheelDistance) < 60) return;
    wheelConsumed = true;
    moveScene(direction);
  }, { passive: false });

  hero.addEventListener("touchstart", (event) => {
    if (event.touches.length !== 1) { touch = null; return; }
    const point = event.touches[0];
    touch = { x: point.clientX, y: point.clientY, distance: 0, axis: null };
  }, { passive: true });
  hero.addEventListener("touchmove", (event) => {
    if (!touch || event.touches.length !== 1) { touch = null; return; }
    const point = event.touches[0];
    const distance = touch.x - point.clientX;
    const verticalDistance = touch.y - point.clientY;
    if (!touch.axis) {
      if (Math.max(Math.abs(distance), Math.abs(verticalDistance)) < 10) return;
      touch.axis = Math.abs(distance) > Math.abs(verticalDistance) ? "x" : "y";
    }
    if (touch.axis !== "x") return;
    event.preventDefault();
    event.stopPropagation();
    touch.distance = distance;
  }, { passive: false });
  hero.addEventListener("touchend", () => {
    if (touch && Math.abs(touch.distance) >= 50) moveScene(Math.sign(touch.distance));
    touch = null;
  });
  hero.addEventListener("touchcancel", () => { touch = null; });

  document.addEventListener("visibilitychange", syncPlayback);
  new IntersectionObserver(([entry]) => {
    inView = entry.isIntersecting && entry.intersectionRatio > 0;
    syncPlayback();
  }, { threshold: [0, 0.01] }).observe(hero);

  controls.hidden = false;
  showScene(0, { animate: false });
  syncPlayback();
}

function setupPageScreens(pages) {
  const screens = [...pages.querySelectorAll(":scope > [data-home-screen]")];
  if (screens.length < 2) return;

  const WHEEL_THRESHOLD = 28;
  const GESTURE_IDLE_MS = 90;
  const SCROLL_DURATION_MS = 480;
  const TRANSITION_LOCK_MS = SCROLL_DURATION_MS;
  const MOMENTUM_TAIL_PX = 8;
  const RENEWED_GESTURE_PX = 16;
  const SUSTAINED_WHEEL_PX = 40;
  const EDGE_TOLERANCE = 2;
  let current = 0;
  let lockedUntil = 0;
  let settleTimer;
  let scrollFrame = null;
  let lastWheelAt = -Infinity;
  let lastWheelTurnAt = -Infinity;
  let lastWheelMagnitude = 0;
  let lastWheelDirection = 0;
  let wheelTailSeen = false;
  let wheelDistance = 0;
  let gestureConsumed = false;
  let gestureScrolledContent = false;
  let touch = null;

  function nearestScreen() {
    return screens.reduce((nearest, screen, index) =>
      Math.abs(screen.offsetTop - pages.scrollTop) < Math.abs(screens[nearest].offsetTop - pages.scrollTop)
        ? index : nearest, 0);
  }

  function recordScreen(index) {
    current = index;
    pages.dataset.activeScreen = screens[index].id;
  }

  function canScrollContent(screen, direction) {
    return direction > 0
      ? screen.scrollTop + screen.clientHeight < screen.scrollHeight - EDGE_TOLERANCE
      : screen.scrollTop > EDGE_TOLERANCE;
  }

  function scrollToScreen(top, instant) {
    cancelAnimationFrame(scrollFrame);
    const from = pages.scrollTop;
    if (instant || Math.abs(top - from) < 1) {
      pages.scrollTo({ top, behavior: "instant" });
      pages.classList.remove("is-turning");
      scrollFrame = null;
      return;
    }
    const startedAt = performance.now();
    pages.classList.add("is-turning");
    function frame(now) {
      const progress = Math.min((now - startedAt) / SCROLL_DURATION_MS, 1);
      const eased = 1 - (1 - progress) ** 3;
      pages.scrollTo({ top: from + (top - from) * eased, behavior: "instant" });
      if (progress < 1) {
        scrollFrame = requestAnimationFrame(frame);
      } else {
        scrollFrame = null;
        pages.classList.remove("is-turning");
      }
    }
    scrollFrame = requestAnimationFrame(frame);
  }

  function goTo(index, { instant = false, atEnd = false, focus = false } = {}) {
    index = Math.max(0, Math.min(screens.length - 1, index));
    const screen = screens[index];
    if (focus || (index !== current && screens[current].contains(document.activeElement))) {
      screen.focus({ preventScroll: true });
    }
    screen.scrollTop = atEnd ? screen.scrollHeight - screen.clientHeight : 0;
    recordScreen(index);
    lockedUntil = performance.now() + (instant ? 0 : TRANSITION_LOCK_MS);
    scrollToScreen(screen.offsetTop, instant);
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => recordScreen(nearestScreen()), TRANSITION_LOCK_MS + 20);
  }

  // Ignore decaying momentum, but let fresh input retarget an animation immediately.
  pages.addEventListener("wheel", (event) => {
    if (event.ctrlKey || !event.deltaY || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
    const now = performance.now();
    const direction = Math.sign(event.deltaY);
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? pages.clientHeight : 1;
    const delta = event.deltaY * unit;
    const magnitude = Math.abs(delta);
    const reversing = lastWheelDirection && direction !== lastWheelDirection;
    const renewed = wheelTailSeen && magnitude >= RENEWED_GESTURE_PX && magnitude > lastWheelMagnitude * 1.5;
    const sustained = now - lastWheelTurnAt >= SCROLL_DURATION_MS
      && magnitude >= SUSTAINED_WHEEL_PX && magnitude >= lastWheelMagnitude;
    if (now - lastWheelAt > GESTURE_IDLE_MS || reversing || renewed || sustained) {
      wheelDistance = 0;
      gestureConsumed = false;
      gestureScrolledContent = false;
      wheelTailSeen = false;
    }
    lastWheelAt = now;
    lastWheelDirection = direction;
    lastWheelMagnitude = magnitude;
    if (magnitude < MOMENTUM_TAIL_PX) wheelTailSeen = true;
    if (gestureConsumed) {
      event.preventDefault();
      return;
    }

    const screen = screens[current];
    if (canScrollContent(screen, direction)) {
      gestureScrolledContent = true;
      wheelDistance = 0;
      if (scrollFrame !== null) {
        event.preventDefault();
        screen.scrollBy({ top: delta, behavior: "instant" });
      }
      return;
    }

    event.preventDefault();
    // Finish reading a tall screen before starting a fresh gesture to leave it.
    if (gestureScrolledContent) return;
    if (Math.sign(wheelDistance) !== direction) wheelDistance = 0;
    wheelDistance += delta;
    if (Math.abs(wheelDistance) < WHEEL_THRESHOLD) return;
    gestureConsumed = true;
    wheelTailSeen = false;
    if (current + direction >= 0 && current + direction < screens.length) {
      lastWheelTurnAt = now;
      goTo(current + direction, { atEnd: direction < 0 });
    }
  }, { passive: false });

  pages.addEventListener("touchstart", (event) => {
    if (event.touches.length !== 1) { touch = null; return; }
    const point = event.touches[0];
    touch = {
      x: point.clientX, y: point.clientY, distance: 0, captured: false,
      atTop: !canScrollContent(screens[current], -1),
      atBottom: !canScrollContent(screens[current], 1),
    };
  }, { passive: true });
  pages.addEventListener("touchmove", (event) => {
    if (!touch || event.touches.length !== 1) { touch = null; return; }
    const point = event.touches[0];
    const distance = touch.y - point.clientY;
    if (Math.abs(distance) < 10 || Math.abs(distance) < Math.abs(point.clientX - touch.x)) return;
    if (distance > 0 ? touch.atBottom : touch.atTop) {
      event.preventDefault();
      touch.captured = true;
      touch.distance = distance;
    }
  }, { passive: false });
  pages.addEventListener("touchend", () => {
    if (touch?.captured && Math.abs(touch.distance) >= 50) {
      const direction = Math.sign(touch.distance);
      if (current + direction >= 0 && current + direction < screens.length) {
        goTo(current + direction, { atEnd: direction < 0 });
      }
    }
    touch = null;
  });
  pages.addEventListener("touchcancel", () => { touch = null; });

  window.addEventListener("keydown", (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.target.closest("input, textarea, select, [contenteditable='true']")) return;
    const key = event.key;
    if (!["ArrowDown", "ArrowUp", "PageDown", "PageUp", " ", "Home", "End"].includes(key)) return;
    if (["ArrowDown", "ArrowUp", " "].includes(key) && event.target.closest("a, button, summary")) return;
    if (event.shiftKey && key !== " ") return;
    event.preventDefault();
    if (event.repeat && performance.now() < lockedUntil) return;
    if (key === "Home" || key === "End") {
      goTo(key === "Home" ? 0 : screens.length - 1, { atEnd: key === "End", focus: true });
      return;
    }
    const direction = key === "ArrowUp" || key === "PageUp" || (key === " " && event.shiftKey) ? -1 : 1;
    if (canScrollContent(screens[current], direction)) {
      screens[current].scrollBy({
        top: direction * (key.startsWith("Arrow") ? 48 : pages.clientHeight * 0.85),
        behavior: "smooth",
      });
    } else if (!event.repeat && current + direction >= 0 && current + direction < screens.length) {
      goTo(current + direction, { atEnd: direction < 0, focus: true });
    }
  });

  pages.addEventListener("scroll", () => {
    if (performance.now() >= lockedUntil) recordScreen(nearestScreen());
  }, { passive: true });

  function followHash(instant) {
    let id;
    try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
    const screen = document.getElementById(id)?.closest("[data-home-screen]");
    const index = screens.indexOf(screen);
    if (index >= 0) goTo(index, { instant });
  }
  window.addEventListener("hashchange", () => followHash(false));

  let width = pages.clientWidth;
  let height = pages.clientHeight;
  new ResizeObserver(() => {
    if (width === pages.clientWidth && height === pages.clientHeight) return;
    width = pages.clientWidth;
    height = pages.clientHeight;
    scrollToScreen(screens[current].offsetTop, true);
  }).observe(pages);
  recordScreen(nearestScreen());
  requestAnimationFrame(() => followHash(true));
}
